// Dietary attribute tooltip explanations
export interface DietaryTooltipInfo {
  label: string;
  description: string;
  matchExplanation: string;
  conflictExplanation?: string;
}

export const dietaryTooltips: Record<string, DietaryTooltipInfo> = {
  'vegan': {
    label: 'Vegan',
    description: 'Contains no animal products including meat, dairy, eggs, or honey',
    matchExplanation: 'This item is vegan-friendly and matches your vegan dietary preference',
    conflictExplanation: 'This item contains animal products and conflicts with your vegan diet'
  },
  'vegetarian': {
    label: 'Vegetarian',
    description: 'Contains no meat but may include dairy, eggs, or honey',
    matchExplanation: 'This item is vegetarian-friendly and matches your vegetarian dietary preference',
    conflictExplanation: 'This item contains meat and conflicts with your vegetarian diet'
  },
  'pescatarian': {
    label: 'Pescatarian',
    description: 'Contains fish and seafood but no other meat',
    matchExplanation: 'This item is pescatarian-friendly and matches your pescatarian dietary preference',
    conflictExplanation: 'This item contains meat other than fish and conflicts with your pescatarian diet'
  },
  'gluten_free': {
    label: 'Gluten Allergy',
    description: 'Contains no gluten, a protein found in wheat, barley, and rye',
    matchExplanation: 'This item is gluten-free and safe for your gluten allergy',
    conflictExplanation: 'This item contains gluten and may trigger your gluten allergy'
  },
  'dairy_free': {
    label: 'Dairy Allergy',
    description: 'Contains no dairy products including milk, cheese, butter, or cream',
    matchExplanation: 'This item is dairy-free and safe for your dairy allergy',
    conflictExplanation: 'This item contains dairy and may trigger your dairy allergy'
  },
  'nut_free': {
    label: 'Nut Allergy',
    description: 'Contains no tree nuts including almonds, walnuts, cashews, or pecans',
    matchExplanation: 'This item is nut-free and safe for your nut allergy',
    conflictExplanation: 'This item contains nuts and may trigger your nut allergy'
  },
  'egg_free': {
    label: 'Egg Allergy',
    description: 'Contains no eggs or egg products',
    matchExplanation: 'This item is egg-free and safe for your egg allergy',
    conflictExplanation: 'This item contains eggs and may trigger your egg allergy'
  },
  'soy_free': {
    label: 'Soy Allergy',
    description: 'Contains no soy or soy products including tofu, soy sauce, or edamame',
    matchExplanation: 'This item is soy-free and safe for your soy allergy',
    conflictExplanation: 'This item contains soy and may trigger your soy allergy'
  },
  'shellfish_free': {
    label: 'Shellfish Allergy',
    description: 'Contains no shellfish including shrimp, crab, lobster, or clams',
    matchExplanation: 'This item is shellfish-free and safe for your shellfish allergy',
    conflictExplanation: 'This item contains shellfish and may trigger your shellfish allergy'
  },
  'fish_free': {
    label: 'Fish Allergy',
    description: 'Contains no fish including salmon, tuna, cod, or sardines',
    matchExplanation: 'This item is fish-free and safe for your fish allergy',
    conflictExplanation: 'This item contains fish and may trigger your fish allergy'
  },
  'sesame_free': {
    label: 'Sesame Allergy',
    description: 'Contains no sesame seeds, tahini, or sesame oil',
    matchExplanation: 'This item is sesame-free and safe for your sesame allergy',
    conflictExplanation: 'This item contains sesame and may trigger your sesame allergy'
  },
  'low_fodmap': {
    label: 'Low FODMAP',
    description: 'Contains low amounts of fermentable carbohydrates that can cause digestive issues',
    matchExplanation: 'This item is low in FODMAPs and suitable for your digestive health',
    conflictExplanation: 'This item is high in FODMAPs and may cause digestive discomfort'
  },
  'keto': {
    label: 'Keto',
    description: 'Contains minimal carbohydrates and is suitable for ketogenic diet',
    matchExplanation: 'This item fits your ketogenic diet with low carb content',
    conflictExplanation: 'This item is high in carbohydrates and doesn\'t fit your keto diet'
  },
  'paleo': {
    label: 'Paleo',
    description: 'Contains only foods that would have been available to early humans',
    matchExplanation: 'This item fits your paleo diet with natural, unprocessed ingredients',
    conflictExplanation: 'This item contains processed foods not suitable for paleo diet'
  },
  'atkins': {
    label: 'Atkins',
    description: 'Contains low carbohydrates and is suitable for Atkins diet',
    matchExplanation: 'This item fits your Atkins diet with controlled carb content',
    conflictExplanation: 'This item is too high in carbohydrates for your Atkins diet'
  },
  'corn_free': {
    label: 'Corn Allergy',
    description: 'Contains no corn or corn products including corn syrup, cornstarch, or cornmeal',
    matchExplanation: 'This item is corn-free and safe for your corn allergy',
    conflictExplanation: 'This item contains corn and may trigger your corn allergy'
  },
  'mustard_free': {
    label: 'Mustard Allergy',
    description: 'Contains no mustard seeds, mustard powder, or mustard oil',
    matchExplanation: 'This item is mustard-free and safe for your mustard allergy',
    conflictExplanation: 'This item contains mustard and may trigger your mustard allergy'
  }
};

// Helper function to get tooltip text based on context
export const getDietaryTooltip = (
  key: string, 
  isUserPreference: boolean, 
  isMatch: boolean = true
): string => {
  const tooltip = dietaryTooltips[key];
  if (!tooltip) return 'Dietary attribute';

  if (isUserPreference && isMatch) {
    return tooltip.matchExplanation;
  } else if (isUserPreference && !isMatch && tooltip.conflictExplanation) {
    return tooltip.conflictExplanation;
  } else {
    return tooltip.description;
  }
};

// Helper function to get detailed tooltip for conflicts
export const getConflictTooltip = (
  key: string,
  ingredientName?: string
): string => {
  const tooltip = dietaryTooltips[key];
  if (!tooltip) return 'Dietary conflict detected';

  const baseExplanation = tooltip.conflictExplanation || tooltip.description;
  return ingredientName 
    ? `${ingredientName}: ${baseExplanation}`
    : baseExplanation;
};

// Helper function to get detailed tooltip for matches
export const getMatchTooltip = (
  key: string,
  ingredientName?: string
): string => {
  const tooltip = dietaryTooltips[key];
  if (!tooltip) return 'Dietary match found';

  const baseExplanation = tooltip.matchExplanation;
  return ingredientName 
    ? `${ingredientName}: ${baseExplanation}`
    : baseExplanation;
};
